import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
    getTicket,
    getAllServices,
    createQueueWithServices,
    getServiceSettings,
} from "../../api/api";
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Link as MuiLink,
    CircularProgress,
    Skeleton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "../../assests/logo.png";
import Cookies from "js-cookie";
import { getFrontendUrl, getStorageUrl } from "../../config/config";
import ScrollButtons from "../../components/ScrollButtons";

const GetTicket = () => {
    const { code: codeFromParams } = useParams();
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [branchId, setBranchId] = useState(null);
    const [logoFilename, setLogoFilename] = useState(null);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const [serviceSelectionText, setServiceSelectionText] = useState("We only have one queue, but if we know what you need help with we can assist you better. You can choose multiple services.");
    const [nextButtonText, setNextButtonText] = useState("Next");

    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [queueNumber, setQueueNumber] = useState(null);

    // Fallback: Extract code from URL if useParams doesn't work (mobile issue)
    const getCodeFromUrl = () => {
        if (codeFromParams) {
            return codeFromParams;
        }
        // Fallback: try to extract from window location
        const path = window.location.pathname;
        const match = path.match(/\/(?:getticket|get-ticket)\/([^\/]+)/i);
        if (match && match[1]) {
            return decodeURIComponent(match[1]);
        }
        return null;
    };

    const code = getCodeFromUrl();

    // Declare all hooks first (React Rules of Hooks requirement)
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [isCheckingRedirect, setIsCheckingRedirect] = useState(true); // Show loading while checking

    // Get logo URL
    const logoUrl = logoFilename
        ? getStorageUrl(`/storage/logos/${logoFilename}`)
        : (loading ? null : Logo);

    // Load text settings (service selection text and Next button text) when branchId is available
    useEffect(() => {
        const fetchTextSettings = async () => {
            if (!branchId) return;
            try {
                const response = await getServiceSettings(branchId);
                if (response.success && response.data) {
                    if (response.data.service_selection_text) {
                        setServiceSelectionText(response.data.service_selection_text);
                    }
                    if (response.data.next_button_text) {
                        setNextButtonText(response.data.next_button_text);
                    }
                }
            } catch (err) {
                console.error("Error fetching service text settings:", err);
            }
        };
        fetchTextSettings();
    }, [branchId]);

    // Preload logo for faster loading (only for remote URLs, not local imports)
    useEffect(() => {
        if (logoUrl && logoFilename && logoUrl.startsWith('http')) {
            try {
                // Create link element for preloading
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = logoUrl;
                link.fetchPriority = 'high';
                document.head.appendChild(link);

                // Also preconnect to the domain for faster connection
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = new URL(logoUrl).origin;
                document.head.appendChild(preconnect);

                return () => {
                    // Cleanup on unmount
                    if (document.head.contains(link)) {
                        document.head.removeChild(link);
                    }
                    if (document.head.contains(preconnect)) {
                        document.head.removeChild(preconnect);
                    }
                };
            } catch (error) {
                console.warn('Error preloading logo:', error);
            }
        }
    }, [logoUrl, logoFilename]);

    // Reset logo loaded state when logoUrl changes
    useEffect(() => {
        setLogoLoaded(false);
    }, [logoUrl]);

    const hasFetchedTicket = useRef(false);

    // SYNCHRONOUS REDIRECT CHECK: Use useLayoutEffect to check cookie and redirect BEFORE paint
    // This runs synchronously before browser paints, preventing any flash
    useLayoutEffect(() => {
        if (!code) {
            setIsCheckingRedirect(false);
            return;
        }
        
        try {
            const existingTicketDetails = Cookies.get("ticketDetails");
            if (existingTicketDetails) {
                const ticketData = JSON.parse(existingTicketDetails);
                
                // Check if service selection is disabled and we have valid data
                if (ticketData.client?.service_selection_enabled === false && 
                    ticketData.queue_number && 
                    ticketData.ticket_code === code) {
                    console.log('🚫 Service selection disabled in cookie (sync check) - redirecting immediately via window.location');
                    
                    // Store state in sessionStorage for getqueue to read
                    const redirectState = {
                        selectedOptions: [], 
                        queueNumber: ticketData.queue_number,
                        serviceSelectionDisabled: true,
                        ticketData: ticketData
                    };
                    sessionStorage.setItem('getticket_redirect_state', JSON.stringify(redirectState));
                    
                    // Use window.location.replace for immediate redirect (happens before React renders)
                    window.location.replace('/get-queue');
                    setIsRedirecting(true);
                    return; // Don't set isCheckingRedirect to false - we're redirecting
                }
            }
        } catch (error) {
            // Ignore cookie parsing errors, will be handled in useEffect
        }
        
        // If we reach here, no redirect is needed - show the page
        setIsCheckingRedirect(false);
    }, [code]);

    // Fix scrolling issue by overriding global overflow hidden
    useEffect(() => {
        // Override global overflow hidden for this page
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;

        // Enable scrolling on body and html
        document.body.style.overflow = 'auto';
        document.body.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';

        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }

        // Add a CSS class to ensure scrolling works
        document.body.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');

        return () => {
            // Restore original overflow settings when component unmounts
            document.body.style.overflow = 'hidden';
            document.body.style.height = '100%';
            htmlElement.style.overflow = 'hidden';
            htmlElement.style.height = '100%';
            if (rootElement) {
                rootElement.style.overflow = 'hidden';
                rootElement.style.height = '100%';
            }
            document.body.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);

    useEffect(() => {
        // Don't run if we're redirecting
        if (isRedirecting) {
            return;
        }

        // Log code for debugging
        console.log('🔍 GetTicket - Code from URL:', code);
        console.log('🔍 GetTicket - hasFetchedTicket:', hasFetchedTicket.current);

        if (!code) {
            console.error('❌ GetTicket - No code parameter found in URL');
            setError("The ticket code is missing from the URL. Please check that the URL is correct.");
            setLoading(false);
            return;
        }

        if (code && !hasFetchedTicket.current) {
            hasFetchedTicket.current = true;

            // Check if we already have a valid queue number from cookies
            const existingTicketDetails = Cookies.get("ticketDetails");
            let ticketData = null;
            if (existingTicketDetails) {
                try {
                    ticketData = JSON.parse(existingTicketDetails);
                    console.log('🍪 Cookie found:', {
                        ticket_code: ticketData.ticket_code,
                        current_code: code,
                        has_client: !!ticketData.client,
                        queue_status: ticketData.queue_status
                    });

                    // IMPORTANT: Clear cookie if ticket code doesn't match (different client)
                    if (ticketData.ticket_code && ticketData.ticket_code !== code) {
                        console.log('⚠️ Cookie from different client detected, clearing cookie');
                        Cookies.remove("ticketDetails", { path: "/" });
                        ticketData = null; // Reset to force API call
                    }
                    // Validate cookie data structure - if invalid, clear it
                    else if (!ticketData.client || !ticketData.client.id) {
                        console.log('⚠️ Cookie has invalid structure (missing client data), clearing cookie');
                        Cookies.remove("ticketDetails", { path: "/" });
                        ticketData = null; // Reset to force API call
                    }
                    // EARLY CHECK: If service selection is disabled in cookie, redirect immediately
                    else if (ticketData.client?.service_selection_enabled === false && 
                             ticketData.queue_number && 
                             ticketData.ticket_code === code) {
                        console.log('🚫 Service selection disabled in cookie - redirecting immediately via window.location');
                        // Store state in sessionStorage for getqueue to read
                        const redirectState = {
                            selectedOptions: [], 
                            queueNumber: ticketData.queue_number,
                            serviceSelectionDisabled: true,
                            ticketData: ticketData
                        };
                        sessionStorage.setItem('getticket_redirect_state', JSON.stringify(redirectState));
                        // Use window.location.replace for immediate redirect (happens before React renders)
                        window.location.replace('/get-queue');
                        return; // Exit early, don't show service selection page
                    }
                    // Check if we have a valid queue number and it's for the same ticket code
                    else if (ticketData.queue_number && ticketData.client && ticketData.ticket_code === code) {
                        // Get branch ID from ticket data if available
                        if (ticketData.client?.id) {
                            setBranchId(ticketData.client.id);
                        }

                        // Get logo filename from ticket data if available
                        if (ticketData.client?.logo) {
                            setLogoFilename(ticketData.client.logo);
                        }

                        // Get service selection text if available
                        if (ticketData.client?.service_selection_text) {
                            setServiceSelectionText(ticketData.client.service_selection_text);
                        }

                        // Check the queue status from cookie
                        const queueStatus = ticketData.queue_status;

                        // If queue status is LOST or ASSISTED, clear cookie and create new queue
                        if (queueStatus === 'LOST' || queueStatus === 'ASSISTED') {
                            console.log('Queue is inactive (ASSISTED/LOST), clearing cookie and creating new queue');

                            // Clear the ticket cookie since the queue is no longer active
                            Cookies.remove("ticketDetails", { path: "/" });

                            // Continue to API call to create a new queue (don't return early)
                            ticketData = null; // Reset ticketData so it doesn't interfere
                        }
                        // If queue status is WAITING or SERVING, use cookie data for quick display
                        // but still validate with API call to ensure code is still valid
                        else if (queueStatus === 'WAITING' || queueStatus === 'SERVING') {
                            console.log('Active queue found in cookie, will validate with API');

                            // Set data from cookie for quick display (optimistic update)
                            setQueueNumber(ticketData.queue_number);
                            // Get branch ID from ticket data
                            if (ticketData.client?.id) {
                                setBranchId(ticketData.client.id);
                            }

                            // Get logo filename from ticket data
                            if (ticketData.client?.logo) {
                                setLogoFilename(ticketData.client.logo);
                            }

                            // Get service selection text if available
                            if (ticketData.client?.service_selection_text) {
                                setServiceSelectionText(ticketData.client.service_selection_text);
                            }

                            // Note: Don't return early - always continue to API call to validate
                            // The API call will verify the code is still valid and update cookie if needed
                            // If the code is invalid, the API will return an error and we'll show it
                        }
                    } else {
                        // Cookie exists but doesn't match current code or is invalid, clear it
                        console.log('Invalid cookie data, clearing cookie');
                        Cookies.remove("ticketDetails", { path: "/" });
                        ticketData = null;
                    }
                } catch (error) {
                    console.error("❌ Error parsing existing ticket details:", error);
                    console.error("❌ Cookie data that failed:", existingTicketDetails);
                    // Clear invalid cookie
                    Cookies.remove("ticketDetails", { path: "/" });
                    ticketData = null;
                }
            } else {
                console.log('🍪 No cookie found, will make API call');
            }

            // Always make API call to validate code and get fresh data
            // Even if we have cookie data, we need to verify the code is still valid
            console.log('📞 GetTicket - Making API call with code:', code, 'Has cookie data:', !!ticketData);
            getTicket(code)
                .then((res) => {
                    console.log('✅ GetTicket - API response received:', res);
                    // Validate that the response has a client ID
                    if (!res.client?.id) {
                        console.error('API response missing client ID');
                        setError("Invalid ticket code. Please check the code and try again.");
                        setLoading(false);
                        return;
                    }

                    // IMPORTANT: Check if service selection is disabled FIRST - before any other processing
                    // This ensures immediate redirect without showing the service selection page
                    if (res.client?.service_selection_enabled === false) {
                        console.log('🚫 Service selection disabled (API) - redirecting immediately via window.location');
                        // Get queue number from response or existing data
                        const queueNum = res.queue_number || ticketData?.queue_number;
                        
                        // Store ticket data with code for future validation (including logo info)
                        const ticketDataWithCode = { ...res, ticket_code: code };
                        Cookies.set("ticketDetails", JSON.stringify(ticketDataWithCode), {
                            expires: 0.5, // Expires in 6 hours
                            path: "/",
                        });
                        
                        // Store state in sessionStorage for getqueue to read
                        const redirectState = {
                            selectedOptions: [], 
                            queueNumber: queueNum,
                            serviceSelectionDisabled: true,
                            ticketData: ticketDataWithCode
                        };
                        sessionStorage.setItem('getticket_redirect_state', JSON.stringify(redirectState));
                        
                        // Update queue to confirm it with explicitly empty services (fire and forget)
                        // Don't wait for this - redirect immediately
                        createQueueWithServices(code, null, new Date().toISOString())
                            .catch((err) => {
                                console.error("Error confirming queue:", err);
                                // Ignore error - we're already redirecting
                            });
                        
                        // Use window.location.replace for immediate redirect (happens before React renders)
                        window.location.replace('/get-queue');
                        return; // Exit early - don't process anything else
                    }

                    // IMPORTANT: Validate client ID matches if we had a cookie
                    // This prevents using cookies from different clients
                    if (ticketData && ticketData.client?.id && ticketData.client.id !== res.client.id) {
                        console.log('Client ID mismatch detected, clearing old cookie');
                        Cookies.remove("ticketDetails", { path: "/" });
                    }

                    // Check if this is an existing queue
                    if (res.existing && res.queue_status) {
                        // Store queue status in response for future reference
                        const ticketDataWithCode = { ...res, ticket_code: code };

                        // Store ticket data with code for future validation
                        Cookies.set("ticketDetails", JSON.stringify(ticketDataWithCode), {
                            expires: 0.5, // Expires in 6 hours
                            path: "/",
                        });

                        // Check the queue status
                        const queueStatus = res.queue_status;

                        // If queue status is WAITING or SERVING, show service selection page
                        if (queueStatus === 'WAITING' || queueStatus === 'SERVING') {
                            console.log('Active queue found, showing service selection page');
                            setQueueNumber(res.queue_number);
                        }
                        // Note: If queue status is LOST or ASSISTED, backend now creates a new queue
                        // So res.existing will be false and we'll handle it below
                    }

                    // Handle new queue (either newly created or after inactive queue)
                    if (!res.existing || !res.queue_status) {
                        // New queue created (or old queue was inactive and new one was created)
                        console.log('New queue created or queue regenerated');
                        setQueueNumber(res.queue_number);
                        // Store ticket data with code for future validation
                        const ticketDataWithCode = { ...res, ticket_code: code };
                        Cookies.set("ticketDetails", JSON.stringify(ticketDataWithCode), {
                            expires: 0.5, // Expires in 6 hours
                            path: "/",
                        });
                    }

                    // Set branch ID from response
                    if (res.client?.id) {
                        setBranchId(res.client.id);
                    }

                    // Set logo filename from response
                    if (res.client?.logo) {
                        setLogoFilename(res.client.logo);
                    }

                    // Set service selection text from response
                    if (res.client?.service_selection_text) {
                        setServiceSelectionText(res.client.service_selection_text);
                    }

                    // Note: fetchOptions will handle setting loading to false when branchId is set
                    // If branchId is not set, we need to set loading to false here
                    if (!res.client?.id) {
                        setLoading(false);
                    }
                })
                .catch((err) => {
                    console.error("❌ Error fetching ticket:", err);
                    console.error("❌ Error details:", {
                        code: code,
                        message: err.message,
                        response: err.response,
                        status: err.response?.status,
                        data: err.response?.data
                    });

                    // Provide more specific error messages based on error type
                    let errorMessage = "Could not retrieve the ticket.";

                    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
                        errorMessage = "The request took too long. Check your internet connection and try again.";
                    } else if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error')) {
                        errorMessage = "No internet connection. Check your network and try again.";
                    } else if (err.response?.status === 403 && err.response?.data?.quota_reached) {
                        // Monthly ticket quota for this branch's plan has been reached.
                        errorMessage = err.response.data.error
                            || "This queue is temporarily unavailable. Please try again later.";
                    } else if (err.response?.status === 404) {
                        errorMessage = `Ticket code "${code}" was not found. Please check that the code is correct.`;
                    } else if (err.response?.status === 500) {
                        errorMessage = "Server error. Please try again later.";
                    } else if (err.response?.data?.message) {
                        errorMessage = err.response.data.message;
                    } else if (err.response?.data?.error) {
                        errorMessage = err.response.data.error;
                    }

                    setError(errorMessage);
                    setLoading(false); // Ensure loading is set to false on error
                });
        }
    }, [code, navigate]);

    useEffect(() => {
        // Set a timeout to prevent infinite loading on mobile if branchId is never set
        let timeoutId;
        if (!branchId) {
            timeoutId = setTimeout(() => {
                console.warn("BranchId not set after 5 seconds, setting loading to false");
                setLoading(false);
            }, 5000);
        }

        const fetchOptions = async () => {
            // Wait for branch ID to be available
            if (!branchId) {
                return;
            }

            // Clear timeout if branchId is now available
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            try {
                setLoading(true); // Set loading to true when starting fetch
                const response = await getAllServices(branchId);
                console.log("API: getAllServices response:", response);
                if (response && Array.isArray(response)) {
                    // Options are plain names, so keep the tags for getqueue: a DEMO
                    // ticket has no queue row, so queue-status can't supply them there.
                    try {
                        const tagsByName = {};
                        response.forEach((service) => {
                            tagsByName[service.service_name] = service.service_tag;
                        });
                        sessionStorage.setItem("eq_service_tags", JSON.stringify(tagsByName));
                    } catch (e) {
                        // sessionStorage unavailable (private mode) - fall back to the default text
                    }
                    setOptions(response.map((service) => service.service_name));
                } else {
                    console.warn("Invalid response format from getAllServices:", response);
                    setOptions([]);
                }
            } catch (err) {
                console.error("Failed to load options", err);
                setError("Could not load options.");
                setOptions([]); // Set empty array on error
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();

        // Cleanup timeout on unmount or when branchId changes
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [branchId, isRedirecting]);

    const handleSelect = (option) => {
        setSelectedOptions((prevSelected) =>
            prevSelected.includes(option)
                ? prevSelected.filter((item) => item !== option)
                : [...prevSelected, option]
        );
    };

    const handleNext = async () => {
        try {
            // Always update the queue with selected services (even if using cached data)
            if (selectedOptions.length > 0) {
                // Convert array to comma-separated string
                const serviceNamesString = selectedOptions.join(", ");

                try {
                    // Get current time from UI to ensure consistency
                    const currentUITime = new Date().toISOString();
                    const response = await createQueueWithServices(code, serviceNamesString, currentUITime);
                } catch (error) {
                    console.error("❌ Error updating services:", error);
                    throw error; // Re-throw to be caught by outer try-catch
                }
            }

            // Navigate to get-queue page
            navigate("/get-queue", { state: { selectedOptions, queueNumber } });
        } catch (error) {
            console.error("Error updating queue with services:", error);
            // Still navigate even if service update fails
            navigate("/get-queue", { state: { selectedOptions, queueNumber } });
        }
    };

    // Show loading spinner while checking if we need to redirect
    // This prevents the service selection page from flashing
    if (isCheckingRedirect || isRedirecting) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: '#f5f5f5',
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: "100vh",
                    px: { xs: 2, sm: 3 },
                    py: { xs: 3, sm: 4 },
                    mt: { xs: 6, sm: 10 },
                    mb: 4,
                    maxWidth: "100%",
                    width: "100%",
                    boxSizing: "border-box"
                }}
            >
                <Box mb={{ xs: 2, sm: 3 }}>
                    {!logoLoaded && (
                        <Skeleton
                            variant="rectangular"
                            width={isMobile ? 150 : 300}
                            height={isMobile ? 60 : 120}
                            sx={{ mx: "auto", borderRadius: 1 }}
                        />
                    )}
                    <img
                        src={logoUrl}
                        alt="Logo"
                        loading="eager"
                        fetchPriority="high"
                        style={{
                            width: isMobile ? 150 : 300,
                            maxWidth: "100%",
                            height: "auto",
                            display: logoLoaded ? "block" : "none",
                        }}
                        onLoad={() => setLogoLoaded(true)}
                        onError={(e) => {
                            // Show fallback logo if error
                            if (logoUrl !== Logo) {
                                e.target.src = Logo;
                            }
                            setLogoLoaded(true);
                        }}
                    />
                </Box>

                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    textAlign="center"
                    mb={{ xs: 2, sm: 3 }}
                    sx={{
                        maxWidth: { xs: "100%", sm: 500 },
                        px: { xs: 1, sm: 0 },
                        fontSize: { xs: "0.9rem", sm: "1rem" }
                    }}
                >
                  {serviceSelectionText}
                </Typography>

                {loading ? (
                    <CircularProgress />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Box sx={{
                        width: "100%",
                        maxWidth: { xs: "100%", sm: 400 },
                        px: { xs: 1, sm: 0 }
                    }}>
                        {options.map((option, index) => (
                            <Button
                                key={index}
                                variant={
                                    selectedOptions.includes(option)
                                        ? "contained"
                                        : "outlined"
                                }
                                fullWidth
                                sx={{
                                    mb: 1,
                                    textTransform: "none",
                                    py: { xs: 1.5, sm: 1.75 },
                                    fontSize: { xs: "0.9rem", sm: "1rem" }
                                }}
                                onClick={() => handleSelect(option)}
                            >
                                {option}
                            </Button>
                        ))}
                    </Box>
                )}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleNext}
                    sx={{
                        mt: { xs: 2, sm: 3 },
                        mb: 2,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "auto", sm: 200 },
                        py: { xs: 1.5, sm: 1.25 },
                        fontSize: { xs: "0.9rem", sm: "1rem" }
                    }}
                    fullWidth={isMobile}
                    disabled={selectedOptions.length === 0}
                >
                    {nextButtonText || "Next"}
                </Button>

                <Box mt={{ xs: 3, sm: 4 }} textAlign="center" mb={2}>
                    <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <MuiLink href="#" underline="hover">
                            easyQue
                        </MuiLink>{" "}
                        (c) by Digifront
                    </Typography>
                    <Typography variant="body2" mt={1} sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        <MuiLink
                            href="mailto:sales@digifront.biz"
                            underline="hover"
                        >
                            sales@digifront.biz
                        </MuiLink>
                    </Typography>
                </Box>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default GetTicket;
